// Card stacking + physics sync, extracted from GameBoard.jsx. Cards on
// the table stack by 2D OBB overlap: findStackHeightAt and
// applyComputedY derive each card's rest Y from the interaction order
// of the cards beneath it. tickPhysicsSync steps the Rapier world and
// reconciles mesh transforms with bodies each frame (skipping cards
// the local player is actively dragging or that a remote peer owns).

import { CARD_HEIGHT, CARD_THICKNESS, CARD_WIDTH } from '../../../utils/game/cardMesh';
import { sortCellMembers, FAN_Y } from '../../../utils/game/cellRestLayout';
import { REMOTE_RENDER_DELAY_MS } from '../../../utils/game/cardOwnership';
import { syncMeshFromBody } from '../../../utils/game/physicsWorld';
import { perf } from '../../../utils/perfMonitor';
import * as THREE from 'three';

// Returns the world-Y of the highest card top whose footprint
// contains the given (x, z) point, or the table baseline if no
// card overlaps. excludeId lets the caller skip a specific card
// (e.g., the one being picked up. Its own y shouldn't influence
// how high it gets lifted).
//
// Used by hand-placement and drag handling to guarantee that the
// most recently touched card always ends up on TOP of any stack.
// `exclude` can be a single cardId string, a Set<cardId>, or null.
// Passing a Set lets a multi-card drag skip every member that's
// currently moving together, which prevents a ratchet loop where
// card A lifts to clear card B while B simultaneously lifts to
// clear the newly-raised A, forever. The set-path is a single
// hash lookup per iteration. Same cost as the string-equality
// path so there's no measurable overhead.
export function findStackHeightAt(host, x, z, exclude = null) {
  const baseTop = 0.05 + CARD_THICKNESS / 2;
  const isExcluded = typeof exclude === 'string'
    ? (id) => id === exclude
    : (exclude && typeof exclude.has === 'function')
      ? (id) => exclude.has(id)
      : () => false;
  let highestTop = baseTop;
  for (const [cardId, mesh] of host.meshes) {
    if (isExcluded(cardId)) continue;
    // Per-card footprint: landscape cards (sites, tapped non-sites)
    // occupy H along world X and W along world Z; portrait cards do
    // the opposite. Using the right half-extents per card makes the
    // drag-lift tight against landscape neighbours instead of
    // wrongly clearing space the card doesn't actually occupy.
    const card = mesh.userData?.cardInstance;
    const landscape = !!card && (Boolean(card.isSite) !== Boolean(card.tapped));
    const halfX = landscape ? CARD_HEIGHT / 2 : CARD_WIDTH / 2;
    const halfZ = landscape ? CARD_WIDTH / 2 : CARD_HEIGHT / 2;
    const dx = Math.abs(mesh.position.x - x);
    const dz = Math.abs(mesh.position.z - z);
    if (dx < halfX && dz < halfZ) {
      const top = mesh.position.y + CARD_THICKNESS / 2;
      if (top > highestTop) highestTop = top;
    }
  }
  return highestTop;
}

/**
 * Allocate a fresh interaction order. Bumped whenever a card is
 * clicked, drag-started, or played from hand. Assigning the new
 * value to that card's cardInstance.interactionOrder makes it the
 * top of any stack at its XZ from the next render frame onward.
 * Both clients arrive at the same Y because both apply identical
 * orders to identical cards (broadcast via card:claim's payload).
 */
// Per-frame physics + multiplayer sync. Runs after stepPhysics inside
// the scene's render loop. For each card mesh:
//   - remote-owned: read an interpolated snapshot from the buffer at
//     (now - render delay) and apply it to the body
//   - free or local-owned: let local physics drive the body
//   - free + just woke: a local action's collision must have woken
//     it (remote bodies are non-colliding), so claim ownership and
//     broadcast a card:claim
//   - local-owned: throttled-broadcast the body's pose at ~30 Hz, and
//     release ownership when the body finally sleeps
export function nextInteractionOrder(host) {
  host._interactionCounter += 1;
  return host._interactionCounter;
}

/**
 * Build a 2D oriented bounding box for a card mesh in the world XZ
 * plane.
 *
 * The card geometry is `BoxGeometry(WIDTH, HEIGHT, THICKNESS)`.
 * local +X is width, local +Y is height, local +Z is thickness
 * (the face normal). The mesh is then rotated -π/2 around X to lie
 * flat on the table, after which local +Y points along world Z and
 * local +Z points along world Y. So the in-plane axes we project
 * for SAT are local +X (width) and local +Y (height), NOT local +Z
 * (which post-rotation is vertical and contributes nothing to a
 * footprint computed in world XZ).
 *
 * Tap (90° Y-rotation) compounds on top. The quaternion handles
 * both, so reading the world XZ projection of local +X and +Y
 * gives the correct footprint axes for any orientation.
 */
export function cardOBB2D(host, mesh) {
  if (!host._obbScratch) host._obbScratch = new THREE.Vector3();
  const v = host._obbScratch;
  v.set(1, 0, 0).applyQuaternion(mesh.quaternion);
  const axx = v.x, axz = v.z;
  v.set(0, 1, 0).applyQuaternion(mesh.quaternion);
  const azx = v.x, azz = v.z;
  return {
    cx: mesh.position.x,
    cz: mesh.position.z,
    axx, axz,
    azx, azz,
    hx: CARD_WIDTH / 2,
    hz: CARD_HEIGHT / 2,
  };
}

/**
 * 2D OBB-OBB intersection test via the Separating Axis Theorem.
 * Two convex rectangles overlap iff for every candidate axis, the
 * projected center-to-center distance is ≤ the sum of half-projected
 * extents. For two rectangles only 4 axes matter. Each rectangle's
 * two local axes. Because any separating axis must be parallel to
 * one of those (Christer Ericson, Real-Time Collision Detection).
 *
 * Strict `>` for the separation test means touching edges count as
 * overlap, so even a sliver of contact registers. Which is what the
 * stacking rule wants ("every pixel of a card is its space").
 */
export function obbsOverlap2D(host, A, B) {
  const dx = B.cx - A.cx;
  const dz = B.cz - A.cz;
  // Test against A's local X axis
  {
    const ux = A.axx, uz = A.axz;
    const projD = Math.abs(dx * ux + dz * uz);
    const projA = A.hx * Math.abs(A.axx * ux + A.axz * uz)
                + A.hz * Math.abs(A.azx * ux + A.azz * uz);
    const projB = B.hx * Math.abs(B.axx * ux + B.axz * uz)
                + B.hz * Math.abs(B.azx * ux + B.azz * uz);
    if (projD > projA + projB) return false;
  }
  // A's local Z axis
  {
    const ux = A.azx, uz = A.azz;
    const projD = Math.abs(dx * ux + dz * uz);
    const projA = A.hx * Math.abs(A.axx * ux + A.axz * uz)
                + A.hz * Math.abs(A.azx * ux + A.azz * uz);
    const projB = B.hx * Math.abs(B.axx * ux + B.axz * uz)
                + B.hz * Math.abs(B.azx * ux + B.azz * uz);
    if (projD > projA + projB) return false;
  }
  // B's local X axis
  {
    const ux = B.axx, uz = B.axz;
    const projD = Math.abs(dx * ux + dz * uz);
    const projA = A.hx * Math.abs(A.axx * ux + A.axz * uz)
                + A.hz * Math.abs(A.azx * ux + A.azz * uz);
    const projB = B.hx * Math.abs(B.axx * ux + B.axz * uz)
                + B.hz * Math.abs(B.azx * ux + B.azz * uz);
    if (projD > projA + projB) return false;
  }
  // B's local Z axis
  {
    const ux = B.azx, uz = B.azz;
    const projD = Math.abs(dx * ux + dz * uz);
    const projA = A.hx * Math.abs(A.axx * ux + A.axz * uz)
                + A.hz * Math.abs(A.azx * ux + A.azz * uz);
    const projB = B.hx * Math.abs(B.axx * ux + B.axz * uz)
                + B.hz * Math.abs(B.azx * ux + B.azz * uz);
    if (projD > projA + projB) return false;
  }
  return true;
}

/**
 * Diagnostic: print the live stack state for every card on the
 * table. Id, name, rank, expected Y, actual mesh.position.y,
 * and renderOrder. Hook to window so you can call it from the
 * devtools console mid-game.
 */
export function dumpStacks(host) {
  const TABLE_Y = 0.05;
  const LAYER_GAP = 0.005;
  const sorted = [];
  for (const [id, mesh] of host.meshes) {
    const card = mesh.userData?.cardInstance;
    if (!card) continue;
    sorted.push({ id, mesh, card, order: card.interactionOrder ?? 0 });
  }
  sorted.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  const obbs = sorted.map((e) => host._cardOBB2D(e.mesh));
  const ranks = new Array(sorted.length);
  for (let i = 0; i < sorted.length; i++) {
    let maxBelow = -1;
    for (let j = 0; j < i; j++) {
      if (!host._obbsOverlap2D(obbs[i], obbs[j])) continue;
      if (ranks[j] > maxBelow) maxBelow = ranks[j];
    }
    ranks[i] = maxBelow + 1;
  }
  const rows = sorted.map((e, i) => ({
    id: e.id.slice(0, 6),
    name: e.card.name?.slice(0, 16) || '<unnamed>',
    rank: ranks[i],
    order: e.order,
    expectedY: (TABLE_Y + CARD_THICKNESS / 2 + ranks[i] * (CARD_THICKNESS + LAYER_GAP)).toFixed(3),
    actualY: e.mesh.position.y.toFixed(3),
    renderOrder: e.mesh.renderOrder,
  }));
  console.table(rows);
}

/**
 * Per-frame: assign every card a Y derived from a longest-overlap-
 * chain rank. Skipped for:
 *   - cards in a local active drag (this.dragging.draggingIds).
 *     handleMouseMove drives their Y via initialY + liftDelta
 *   - cards owned by the remote peer (mid-drag from their side).
 *     tickPhysicsSync's remote branch drives their Y via the
 *     interpolation buffer
 *
 * The rank rule:
 *   rank(C) = 1 + max(rank(O) for O in overlap(C) where (O.order, O.id)
 *                                                    < (C.order, C.id))
 *   rank(C) = 0 if no such O.
 *
 * This is the longest-decreasing-overlap-chain ending at C. The
 * "LIS-style" rank. The flat count-of-overlaps version was wrong:
 * if C overlaps only its parent P (and not P's grandparents that
 * are offset out of C's footprint), count gives `rank(C) = 1 =
 * rank(P)`, putting them at the same Y. The chain rule forces
 * `rank(C) >= rank(P) + 1` whenever C overlaps P, so two stacked
 * cards in the same chain literally cannot share Y by definition.
 *
 * Computed by sorting cards by (order, id) ascending and filling
 * `ranks` in that order. When we look up rank(O) for O below C,
 * it has already been computed. O(N²) per frame in the worst case
 * but bounded by table card count.
 */
export function applyComputedY(host) {
  const now = performance.now();
  const draggingIds = host.dragging?.draggingIds;

  // 1. Rank every card by its within-cell resting order (Positions D5,
  //    coordinate-free board): region tier, then server-stamped stackSeq.
  //    the same total order layoutCellRest cascades by, so Y and the cascade
  //    slot always agree. Cards with no cell (auras on intersections,
  //    pre-position avatars, an optimistic cast awaiting its confirm) rest at
  //    rank 0, one layer under every cell stack. The tier flip in descend
  //    view keeps subsurface units hoverable above their site (surface units
  //    at tier 0 are hidden by _applyRegionViewVisibility there).
  const inSubsurfaceView = host.state.regionView === 'subsurface';
  const noCell = [];
  const cells = new Map();
  for (const [id, mesh] of host.meshes) {
    const card = mesh.userData?.cardInstance;
    if (!card) continue;
    const col = card.cell?.col;
    const row = card.cell?.row;
    if (card.type === 'Aura' || typeof col !== 'number' || typeof row !== 'number') {
      noCell.push({ id, mesh, card });
      continue;
    }
    const key = `${col},${row}`;
    let arr = cells.get(key);
    if (!arr) cells.set(key, (arr = []));
    arr.push({ id, mesh, card, isSite: !!card.isSite, isToken: !!card.isToken, subsurface: !!card.subsurface, stackSeq: card.stackSeq });
  }
  noCell.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  // 2. Global draw order: cell-less first, then cells by key, members in
  //    rank order. Deterministic across frames and clients (stable
  //    transparent-pass sorting). The FOCUSED cell's members (cell → fan →
  //    act) sort LAST so the lifted fan draws over everything; their Y
  //    target is the fan height, and the shared tween below is what makes
  //    them fly up on focus and drop back on collapse.
  const focusedKey = host.state.focusedCell || null;
  const sortedEntries = [];
  const ranks = [];
  const FAN_RANK = -1;
  for (const e of noCell) {
    sortedEntries.push(e);
    ranks.push(0);
  }
  for (const key of [...cells.keys()].sort()) {
    if (key === focusedKey) continue;
    const members = sortCellMembers(cells.get(key), inSubsurfaceView);
    members.forEach((e, idx) => {
      sortedEntries.push(e);
      ranks.push(1 + idx);
    });
  }
  if (focusedKey && cells.has(focusedKey)) {
    const members = sortCellMembers(cells.get(focusedKey), inSubsurfaceView);
    for (const e of members) {
      sortedEntries.push(e);
      ranks.push(FAN_RANK);
    }
  }

  // 3. Apply renderOrder + Y, with tween for the falling/rising feel.
  const TABLE_Y = 0.05;
  const LAYER_GAP = 0.005;
  for (let i = 0; i < sortedEntries.length; i++) {
    const { id, mesh } = sortedEntries[i];
    mesh.renderOrder = i;
    if (draggingIds && draggingIds.has(id)) continue;
    if (host.ownership.is(id, 'remote')) continue;
    // Combat director owns the card's Y during a lunge (rise-and-slam)
    // Without this exemption the stacking pass stomps the lift on
    // the very next frame and the animation reads as nothing.
    if (mesh.userData._combatAnimating) continue;

    const targetY = ranks[i] === FAN_RANK
      ? FAN_Y
      : TABLE_Y + CARD_THICKNESS / 2 + ranks[i] * (CARD_THICKNESS + LAYER_GAP);
    const distanceToTarget = Math.abs(targetY - mesh.position.y);
    if (distanceToTarget < 0.001) {
      host._yTweens.delete(id);
      continue;
    }
    let tween = host._yTweens.get(id);
    if (!tween || Math.abs(tween.targetY - targetY) > 0.001) {
      tween = { startY: mesh.position.y, targetY, startTime: now };
      host._yTweens.set(id, tween);
    }
    const falling = tween.startY > tween.targetY;
    const duration = falling ? 250 : 120;
    const t = Math.min(1, (now - tween.startTime) / duration);
    const eased = falling ? t * t : 1 - (1 - t) * (1 - t);
    mesh.position.y = tween.startY + (tween.targetY - tween.startY) * eased;
    if (t >= 1) {
      mesh.position.y = tween.targetY;
      host._yTweens.delete(id);
    }
    // Mirror Y onto the kinematic body so next frame's
    // syncMeshFromBody (which copies body→mesh) doesn't reset
    // mesh.y back to whatever the drag handler last set on the
    // body, which would re-trigger the tween indefinitely.
    const body = mesh.userData?.body;
    if (body) body.position.y = mesh.position.y;
  }
}

export function tickPhysicsSync(host) {
  const now = performance.now();
  const renderTime = now - REMOTE_RENDER_DELAY_MS;
  let localCount = 0;
  let remoteCount = 0;

  for (const [cardId, mesh] of host.meshes) {
    const body = mesh.userData?.body;
    if (!body) continue;

    const owner = host.ownership.get(cardId);

    if (owner === 'remote') {
      remoteCount++;
      // If WE are currently dragging this card locally on the non-host,
      // ignore the host's interpolated sample for this frame. Our
      // mesh is being driven by the cursor and the host's echoed pose
      // stream lags us by ~1 RTT.
      const isLocallyDragging = host.dragging
        && host.dragging.draggingIds
        && host.dragging.draggingIds.has(cardId);
      const sample = isLocallyDragging
        ? null
        : host.ownership.sampleAt(cardId, renderTime);
      if (sample) {
        body.position.set(sample.pos[0], sample.pos[1], sample.pos[2]);
        body.quaternion.set(sample.quat[0], sample.quat[1], sample.quat[2], sample.quat[3]);
      }
      syncMeshFromBody(mesh);
      continue;
    }

    // Local or free. Copy body pose to mesh. _applyComputedY runs
    // immediately after this loop and overrides Y from the
    // interactionOrder-based stack rank.
    syncMeshFromBody(mesh);

    if (host.ownership.is(cardId, 'local')) {
      localCount++;
      const pos = [body.position.x, body.position.y, body.position.z];
      const quat = [body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w];

      // Stream pose to the opponent during a drag.
      if (host.ownership.shouldBroadcast(cardId, pos, quat, now)) {
        host._presenceStreamCardPose(cardId, pos, quat);
        host.ownership.recordBroadcast(cardId, pos, quat, now);
        perf.count('sync.pose.send');
      }

      // Release the moment the active drag ends. There's no physics
      // settle to wait for. InteractionOrder + _computeStackedY
      // produce the resting Y deterministically, so the cursor pose
      // at mouseup is the final pose.
      const isDraggingThis = host.dragging
        && (host.dragging.combatDefenderCard?.id === cardId
          || (host.dragging.draggingIds
            ? host.dragging.draggingIds.has(cardId)
            : host.dragging.cardInstance?.id === cardId));
      if (!isDraggingThis) {
        host._presenceRelease(cardId, pos, quat);
        host.ownership.setFree(cardId);
        perf.count('sync.release.send');
        // Reconcile to the authoritative cell slot NOW: the confirming diff
        // often lands DURING the ownership window (livePositions skips owned
        // cards) and a quiet game sends no later diff to heal it. The card
        // would rest wherever the cursor let go. Skipped while a move intent
        // is in flight (the mirror still holds the OLD cell; the confirm diff
        // or TTL sweep owns that landing) and for auras (intersection
        // anchors, not cell-derived). A cast still in flight no-ops inside
        // snapCardToServerCell (mirror zone isn't 'play' yet) and its confirm
        // diff folds after this free. Either order lands on the slot.
        // Free card placement (non-site): "wherever the cursor let go" IS the
        // committed rest pose, and the mirror has no optimistic apply, so
        // reconciling here would glide the card back to its STALE pos until
        // the confirming diff lands. Skip; the diff folds to the drop point
        // (a no-op) and the fold still heals any genuine divergence.
        const inst = mesh.userData?.cardInstance;
        const freeRest = (host._freeCardPlacement?.() || host._automationsEnabled?.() === false)
          && !inst?.isSite && inst?.type !== 'Site';
        if (!freeRest
          && !host._pendingMoves?.has(cardId)
          && !host._combatSelect?.chosen?.has?.(cardId)
          && inst?.type !== 'Aura') {
          host._snapCardToServerCell?.(cardId, { glide: true });
        }
      }
    }
  }

  perf.gauge('mesh.count', host.meshes.size);
  perf.gauge('owner.local', localCount);
  perf.gauge('owner.remote', remoteCount);
}
